import { authorizationHeader, type Session } from '../auth/auth-client';
import { AccountError, problemToAccountError } from './account-client';

export interface LoadoutStats {
  readonly hull: number;
  readonly regen: number;
  readonly damage: number;
  readonly fireRange: number;
  readonly moveSpeed: number;
  readonly passiveScrap: number;
}

export interface LoadoutBuild {
  readonly stats: LoadoutStats;
  readonly slot1AbilityId: number | null;
  readonly slot2AbilityId: number | null;
}

export interface LoadoutView extends LoadoutBuild {
  readonly shipId: number;
  readonly stock: boolean;
  readonly updatedAt: string | null;
  readonly creditsPerPoint: number;
  readonly slot1Pool: readonly number[];
  readonly slot2Pool: readonly number[];
}

export interface LoadoutConfirmResult {
  readonly charged: number | null;
  readonly credit: number;
  readonly loadout: LoadoutView;
}

export interface LoadoutClient {
  get(session: Session, shipId: number): Promise<LoadoutView>;
  confirm(
    session: Session,
    shipId: number,
    build: LoadoutBuild,
    expectedPrice: number | null,
  ): Promise<LoadoutConfirmResult>;
}

function loadoutFrom(body: {
  shipId?: number;
  stock?: boolean;
  stats?: Partial<LoadoutStats>;
  slot1AbilityId?: number | null;
  slot2AbilityId?: number | null;
  updatedAt?: string | null;
  creditsPerPoint?: number;
  slot1Pool?: number[];
  slot2Pool?: number[];
}): LoadoutView {
  const stats = body.stats ?? {};
  return {
    shipId: body.shipId ?? 0,
    stock: body.stock ?? true,
    stats: {
      hull: stats.hull ?? 0,
      regen: stats.regen ?? 0,
      damage: stats.damage ?? 0,
      fireRange: stats.fireRange ?? 0,
      moveSpeed: stats.moveSpeed ?? 0,
      passiveScrap: stats.passiveScrap ?? 0,
    },
    slot1AbilityId: body.slot1AbilityId ?? null,
    slot2AbilityId: body.slot2AbilityId ?? null,
    updatedAt: body.updatedAt ?? null,
    creditsPerPoint: body.creditsPerPoint ?? 0,
    slot1Pool: body.slot1Pool ?? [],
    slot2Pool: body.slot2Pool ?? [],
  };
}

export class HttpLoadoutClient implements LoadoutClient {
  constructor(private readonly baseUrl: string) {}

  async get(session: Session, shipId: number): Promise<LoadoutView> {
    const response = await this.send(`/api/loadouts?shipId=${String(shipId)}`, session, {
      method: 'GET',
    });
    if (!response.ok) throw await this.problemToError(response);
    const body = (await response.json()) as Parameters<typeof loadoutFrom>[0];
    return loadoutFrom(body);
  }

  async confirm(
    session: Session,
    shipId: number,
    build: LoadoutBuild,
    expectedPrice: number | null,
  ): Promise<LoadoutConfirmResult> {
    const response = await this.send('/api/loadouts/confirm', session, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        shipId,
        hull: build.stats.hull,
        regen: build.stats.regen,
        damage: build.stats.damage,
        fireRange: build.stats.fireRange,
        moveSpeed: build.stats.moveSpeed,
        passiveScrap: build.stats.passiveScrap,
        slot1AbilityId: build.slot1AbilityId,
        slot2AbilityId: build.slot2AbilityId,
        expectedPrice,
      }),
    });
    if (!response.ok) throw await this.problemToError(response);
    const body = (await response.json()) as Partial<{
      charged: number | null;
      credit: number;
      loadout: Parameters<typeof loadoutFrom>[0];
    }>;
    return {
      charged: body.charged ?? null,
      credit: body.credit ?? 0,
      loadout: loadoutFrom(body.loadout ?? {}),
    };
  }

  private async send(
    path: string,
    session: Session,
    init: { method: string; headers?: Record<string, string>; body?: string },
  ): Promise<Response> {
    try {
      return await fetch(`${this.baseUrl.replace(/\/+$/, '')}${path}`, {
        method: init.method,
        ...(init.body !== undefined ? { body: init.body } : {}),
        credentials: 'omit',
        headers: { ...init.headers, authorization: authorizationHeader(session) },
      });
    } catch (cause) {
      const error = new AccountError(
        'Could not reach the server. Check your connection and try again.',
        'unreachable',
      );
      error.cause = cause;
      throw error;
    }
  }

  private async problemToError(response: Response): Promise<AccountError> {
    return problemToAccountError(response);
  }
}
